import React, { useState } from 'react';
import {
  AlertTriangle,
  XCircle,
  AlertCircle,
  Info,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Copy,
  X
} from 'lucide-react';

/**
 * ErrorDisplay - Comprehensive error display component with recovery actions
 * Shows user-friendly error messages with expandable details and recovery suggestions
 */
const ErrorDisplay = ({
  error,
  onRetry,
  onDismiss,
  showDetails = false,
  showRecoveryActions = true,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!error) return null;

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'HIGH':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'MEDIUM':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      case 'LOW':
        return <Info className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getSeverityColors = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'HIGH':
        return 'bg-red-50 border-red-200 text-red-700';
      case 'MEDIUM':
        return 'bg-orange-50 border-orange-200 text-orange-700';
      case 'LOW':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  const copyErrorDetails = async () => {
    const errorDetails = {
      message: error.message,
      type: error.type,
      severity: error.severity,
      timestamp: error.timestamp,
      context: error.context
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  };

  return (
    <div className={`rounded-lg border p-4 ${getSeverityColors(error.severity)} ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          {getSeverityIcon(error.severity)}
          <div className="flex-1">
            <h3 className="font-semibold text-sm">
              {error.title || 'Erreur'}
            </h3>
            <p className="text-sm mt-1">
              {error.userMessage || error.description || error.message}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center px-3 py-1 bg-white bg-opacity-80 rounded text-xs font-medium hover:bg-opacity-100 transition-colors"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Réessayer
            </button>
          )}
          
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="p-1 bg-white bg-opacity-80 rounded hover:bg-opacity-100 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Recovery Actions */}
      {showRecoveryActions && error.recoveryActions && error.recoveryActions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-current border-opacity-20">
          <h4 className="text-xs font-semibold mb-2">Actions recommandées:</h4>
          <ul className="text-xs space-y-1">
            {error.recoveryActions.map((action, index) => (
              <li key={index} className="flex items-start">
                <span className="inline-block w-1 h-1 bg-current rounded-full mt-2 mr-2 flex-shrink-0"></span>
                {action}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Expandable Details */}
      {showDetails && (
        <div className="mt-3 pt-3 border-t border-current border-opacity-20">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center text-xs font-medium hover:underline"
          >
            Détails techniques
            {isExpanded ? (
              <ChevronUp className="h-3 w-3 ml-1" />
            ) : (
              <ChevronDown className="h-3 w-3 ml-1" />
            )}
          </button>
          
          {isExpanded && (
            <div className="mt-2 p-3 bg-white bg-opacity-50 rounded text-xs font-mono">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">Informations d'erreur</span>
                <button
                  onClick={copyErrorDetails}
                  className="inline-flex items-center px-2 py-1 bg-white bg-opacity-80 rounded text-xs hover:bg-opacity-100 transition-colors"
                >
                  <Copy className="h-3 w-3 mr-1" />
                  {copied ? 'Copié!' : 'Copier'}
                </button>
              </div>
              
              <div className="space-y-1 text-xs">
                <div><strong>Type:</strong> {error.type}</div>
                <div><strong>Sévérité:</strong> {error.severity}</div>
                {error.timestamp && (
                  <div><strong>Horodatage:</strong> {new Date(error.timestamp).toLocaleString()}</div>
                )}
                {error.context && Object.keys(error.context).length > 0 && (
                  <div>
                    <strong>Contexte:</strong>
                    <pre className="mt-1 text-xs overflow-x-auto">
                      {JSON.stringify(error.context, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * ErrorBoundary - React Error Boundary for catching component errors
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error for monitoring
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-3 mb-4">
            <XCircle className="h-6 w-6 text-red-600" />
            <div>
              <h2 className="text-lg font-semibold text-red-800">
                Erreur de composant
              </h2>
              <p className="text-sm text-red-700">
                Une erreur inattendue s'est produite dans l'interface.
              </p>
            </div>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser la page
            </button>
            
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-red-700">
                  Détails de l'erreur (développement)
                </summary>
                <pre className="mt-2 p-3 bg-white border border-red-200 rounded text-xs overflow-auto">
                  {this.state.error && this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorToast - Lightweight error notification
 */
export const ErrorToast = ({ error, onDismiss, autoHide = true }) => {
  React.useEffect(() => {
    if (autoHide) {
      const timer = setTimeout(() => {
        onDismiss && onDismiss();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [autoHide, onDismiss]);

  return (
    <div className={`
      fixed top-4 right-4 z-50 max-w-sm p-4 rounded-lg shadow-lg border
      ${getSeverityColors(error.severity)}
      animate-in slide-in-from-right duration-300
    `}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-2">
          {getSeverityIcon(error.severity)}
          <div className="flex-1">
            <p className="text-sm font-medium">
              {error.userMessage || error.message}
            </p>
          </div>
        </div>
        
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="ml-2 p-1 hover:bg-white hover:bg-opacity-50 rounded transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Helper function to get severity colors (used by ErrorToast)
 */
const getSeverityColors = (severity) => {
  switch (severity) {
    case 'CRITICAL':
      return 'bg-red-50 border-red-200 text-red-800';
    case 'HIGH':
      return 'bg-red-50 border-red-200 text-red-700';
    case 'MEDIUM':
      return 'bg-orange-50 border-orange-200 text-orange-700';
    case 'LOW':
      return 'bg-yellow-50 border-yellow-200 text-yellow-700';
    default:
      return 'bg-gray-50 border-gray-200 text-gray-700';
  }
};

/**
 * Helper function to get severity icons (used by ErrorToast)
 */
const getSeverityIcon = (severity) => {
  switch (severity) {
    case 'CRITICAL':
      return <XCircle className="h-4 w-4 text-red-600" />;
    case 'HIGH':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'MEDIUM':
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    case 'LOW':
      return <Info className="h-4 w-4 text-yellow-500" />;
    default:
      return <AlertCircle className="h-4 w-4 text-gray-500" />;
  }
};

export default ErrorDisplay;
